import type { Initiative } from '@/domain/types'
import { useApiQuery, type QueryState } from '@/api/useApiQuery'

export interface InitiativeQuery {
  /** ISO 8601 timestamp; renders historical state via FOR SYSTEM_TIME AS OF. */
  asOf?: string
  /** Default 20, max 200 — see api-contracts.md. */
  historyLimit?: number
  /** Show inactive rows (admin only). */
  includeInactive?: boolean
}

/**
 * Fetches GET /api/initiatives/{id}. Detail response includes the `history`
 * array sourced from the temporal table. When `asOf` is provided the API
 * threads it across every joined temporal table so historical owner /
 * capability / domain names are coherent at that timestamp — never
 * Frankenstein rows.
 *
 * `refreshKey` forces a re-fetch even when the path hasn't changed.
 * The detail view bumps it after a successful PATCH so the user
 * doesn't see stale data after editing.
 */
export function useInitiative(
  initiativeId: number,
  query: InitiativeQuery = {},
  refreshKey: number = 0,
): QueryState<Initiative> {
  const params = new URLSearchParams()
  if (query.asOf) params.set('asOf', query.asOf)
  if (query.historyLimit !== undefined) params.set('historyLimit', String(query.historyLimit))
  if (query.includeInactive) params.set('includeInactive', 'true')
  const qs = params.toString()
  const path = qs ? `/api/initiatives/${initiativeId}?${qs}` : `/api/initiatives/${initiativeId}`
  return useApiQuery<Initiative>(path, refreshKey)
}
